/**
 * 订单 / 支付域 API 封装(W-9~W-13)。复用基座 `call<T>()` + `ServerError`(client.ts),
 * **不内联 envelope helper**。类型移植 rn-app `services/orderService.ts`(字段名严格对齐后端)。
 *
 * 端点(后端契约不可改,与 workability-server/openapi.yaml 一致):
 *  - GET  /api/v1/orders/preview?productId=        → OrderPreviewResponse(买家视角,无 commissionAmount)
 *  - POST /api/v1/orders                           → OrderSubmitResponse(state=PENDING_PAYMENT,幂等 by clientUuid)
 *  - POST /api/v1/payment/create  {orderId,method} → OrderPayResponse(MVP method=MOCK;UI 仍呈现微信支付)
 *  - GET  /api/v1/orders?role=&page=&size=         → OrderListResponse(双 tab)
 *  - GET  /api/v1/orders/{orderId}                 → OrderDetail(判别联合,按 viewerRole 窄化)
 *  - POST /api/v1/orders/{orderId}/complete        → OrderCompleteResponse(买家单方确认完成)
 *
 * 字段层硬隔离(FR-C13):买家类型(BuyerOrderDetail)物理上不含抽佣/分账字段;
 * `OrderPreviewResponse` 本身不含 commissionAmount。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];

/* ============== 枚举(与后端 OrderState / OrderSubState 对齐) ============== */
export type OrderStateName = 'PENDING_PAYMENT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type OrderSubStateName = 'NONE' | 'PENDING_SELLER_RESPONSE' | 'IN_ARBITRATION';
export type OrderRole = 'buyer' | 'seller';
/** MVP 仅 MOCK;UI 呈现微信支付(method 路由属后端关切,见 cashier 页 ★决策)。 */
export type PaymentMethodName = 'WECHAT' | 'ALIPAY' | 'MOCK';

/* ============== W-9 下单确认 ============== */
export type OrderPreviewResponse = Schemas['OrderPreviewResponse'];
export type OrderSubmitRequest = Schemas['OrderSubmitRequest'];
export type OrderSubmitResponse = Schemas['OrderSubmitResponse'];

export function getOrderPreview(productId: string): Promise<OrderPreviewResponse> {
  return call<OrderPreviewResponse>({
    method: 'GET',
    url: '/api/v1/orders/preview',
    params: { productId },
  });
}

export function submitOrder(req: OrderSubmitRequest): Promise<OrderSubmitResponse> {
  return call<OrderSubmitResponse>({ method: 'POST', url: '/api/v1/orders', data: req });
}

/* ============== W-10 收银台支付发起 ============== */
export type OrderPayResponse = Schemas['OrderPayResponse'];

export function payOrder(
  orderId: string,
  method: PaymentMethodName,
): Promise<OrderPayResponse> {
  return call<OrderPayResponse>({
    method: 'POST',
    url: '/api/v1/payment/create',
    data: { orderId, method } satisfies Schemas['OrderPayRequest'],
  });
}

/* ============== W-12 订单列表 ============== */
export type CounterpartyDTO = Schemas['CounterpartyDTO'];

/** 列表项(后端字段 optional;前端按非空收敛)。 */
export interface OrderListItem {
  orderId: string;
  productId: string;
  productTitle: string;
  coverImageUrl: string;
  state: OrderStateName;
  subState: OrderSubStateName;
  amount: number; // 分
  createdAt: string;
  counterparty: { userId: string; nickname: string; avatar: string | null };
}

export interface OrderListResponse {
  items: OrderListItem[];
  page: number;
  size: number;
  total: number;
  hasMore: boolean;
}

export interface ListMyOrdersParams {
  role: OrderRole;
  page: number;
  size: number;
}

export async function listMyOrders(params: ListMyOrdersParams): Promise<OrderListResponse> {
  const raw = await call<Schemas['OrderListResponse']>({
    method: 'GET',
    url: '/api/v1/orders',
    params,
  });
  return {
    items: (raw.items ?? []).map(normalizeListItem),
    page: raw.page ?? params.page,
    size: raw.size ?? params.size,
    total: raw.total ?? 0,
    hasMore: raw.hasMore ?? false,
  };
}

function normalizeListItem(it: Schemas['OrderListItemResponse']): OrderListItem {
  return {
    orderId: it.orderId ?? '',
    productId: it.productId ?? '',
    productTitle: it.productTitle ?? '',
    coverImageUrl: it.coverImageUrl ?? '',
    state: (it.state as OrderStateName) ?? 'PENDING_PAYMENT',
    subState: (it.subState as OrderSubStateName) ?? 'NONE',
    amount: it.amount ?? 0,
    createdAt: it.createdAt ?? '',
    counterparty: {
      userId: it.counterparty?.userId ?? '',
      nickname: it.counterparty?.nickname ?? '我行用户',
      avatar: it.counterparty?.avatar ?? null,
    },
  };
}

/* ============== W-13 订单详情(双视角判别联合) ============== */
/**
 * openapi `GET /orders/{id}` 返 ResultObject(data: unknown)—— 无 typed schema,
 * 故本地定义明细类型,字段名严格对齐 rn-app `orderService.ts` / 后端 DTO。
 */
export interface OrderTimelineNode {
  state: OrderStateName;
  subState: OrderSubStateName;
  label: string;
  occurredAt: string | null;
  occurred: boolean;
}

export interface OrderDetailProduct {
  productId: string;
  productTitle: string;
  coverImageUrl: string;
  categoryL1Id: string;
}

export interface OrderDetailSeller {
  sellerId: string;
  nickname: string;
  avatar: string | null;
  rating: number | null;
  salesCount: number | null;
}

export interface OrderDetailBuyer {
  buyerId: string;
  nickname: string;
  avatar: string | null;
}

/** 取消申请上下文(卖家视角;subState ∈ {PENDING_SELLER_RESPONSE, IN_ARBITRATION})。 */
export interface CancellationContext {
  cancellationId: string;
  reasonCode: 'SELLER_BREACH' | 'NOT_AS_DESCRIBED' | 'CHANGED_MIND' | 'OTHER';
  description: string;
  evidenceUrls: string[];
  createdAt: string;
  deadlineAt: string;
}

/** 仲裁上下文(双视角;不含 rulingReasonText,FR-C13)。 */
export interface ArbitrationContext {
  cancellationId: string;
  arbitrationTaskId: string;
  status: 'PENDING' | 'RULED';
  slaDeadlineAt: string;
  deadlineRemainingSec: number;
  ruling: 'CANCEL_AGREED' | 'ORDER_UPHELD' | null;
  ruledAt: string | null;
}

/** 买家视角:绝不含 commission/seller-amount/T+7 字段(字段层硬隔离)。 */
export interface BuyerOrderDetail {
  viewerRole: 'BUYER';
  orderId: string;
  state: OrderStateName;
  subState: OrderSubStateName;
  amount: number; // 分(文案"合计支付")
  createdAt: string;
  remark: string | null;
  product: OrderDetailProduct;
  seller: OrderDetailSeller;
  timeline: OrderTimelineNode[];
  cancelPolicyText: string;
  reviewable?: boolean;
  reviewed?: boolean;
  /** 后置售后可申请(COMPLETED 且在确认完成后 T+7 窗口内;与 reviewable 正交)。 */
  aftersaleable?: boolean;
  /** 售后申请截止时刻(ISO-8601);后端计算,前端只读展示,不自行算窗口。 */
  aftersaleDeadlineAt?: string | null;
  arbitrationContext?: ArbitrationContext | null;
  /** 走查修复:买家提交的取消/售后单上下文(自陈原因/说明/凭证/卖家响应截止),供订单详情展示"售后单记录"。 */
  cancellationContext?: CancellationContext | null;
}

/** 卖家视角:含完整分账明细。 */
export interface SellerOrderDetail {
  viewerRole: 'SELLER';
  orderId: string;
  state: OrderStateName;
  subState: OrderSubStateName;
  amount: number; // 分(文案"订单总额")
  createdAt: string;
  remark: string | null;
  product: OrderDetailProduct;
  buyer: OrderDetailBuyer;
  timeline: OrderTimelineNode[];
  cancelPolicyText: string;
  commissionAmount: number;
  sellerAmount: number;
  t7CountdownSec: number | null;
  t7AnchorAt: string | null;
  settledAmount: number;
  cancellationContext?: CancellationContext | null;
  arbitrationContext?: ArbitrationContext | null;
}

export type OrderDetail = BuyerOrderDetail | SellerOrderDetail;

export async function getOrderDetail(orderId: string): Promise<OrderDetail> {
  const raw = await call<OrderDetail>({
    method: 'GET',
    url: `/api/v1/orders/${encodeURIComponent(orderId)}`,
  });
  return normalizeDetail(raw);
}

/**
 * 兜底归一:openapi 该端点无 typed schema(ResultObject),后端字段理论必下发但运行时不保证。
 * 金额缺省归 0(防 `¥NaN`)、timeline 缺省 []、分账字段缺省 0,避免脏数据击穿 UI。
 */
function normalizeDetail(d: OrderDetail): OrderDetail {
  const base = {
    ...d,
    amount: d.amount ?? 0,
    timeline: Array.isArray(d.timeline) ? d.timeline : [],
  };
  if (base.viewerRole === 'SELLER') {
    const s = base as SellerOrderDetail;
    return {
      ...s,
      commissionAmount: s.commissionAmount ?? 0,
      sellerAmount: s.sellerAmount ?? 0,
      settledAmount: s.settledAmount ?? 0,
    };
  }
  return base;
}

/* ============== W-13 买家确认完成 ============== */
export interface OrderCompleteResponse {
  orderId: string;
  state: string; // "COMPLETED"
  completedAt: string;
  settleDeadlineAt: string;
}

export function completeOrder(orderId: string): Promise<OrderCompleteResponse> {
  return call<OrderCompleteResponse>({
    method: 'POST',
    url: `/api/v1/orders/${encodeURIComponent(orderId)}/complete`,
    data: {},
  });
}
